import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {RootState} from "../../../app/store";
import {RequestStatus} from "../../../api/apiConfiguration";
import {
    deleteKnowledgeDefinition as deleteKnowledgeDefinitionRequest,
    fetchKnowledgeAttributeDefinitions,
    fetchKnowledgeTypeDefinitions,
    getKnowledgeDefinitionName,
    KnowledgeAttributeDefinitionUi,
    KnowledgeDefinitionKind,
    KnowledgeDefinitionRequest,
    KnowledgeDefinitionUi,
    KnowledgeTypeDefinitionUi,
    saveKnowledgeDefinition as saveKnowledgeDefinitionRequest
} from "./knowledgeDefinitionsApi";

/**
 * The catalogue of knowledge types and attributes. The settings page manages it, and every
 * Knowledge tab reads it for the type dropdown and the attributes a record can carry.
 */
export interface KnowledgeDefinitionsState {
    types: KnowledgeTypeDefinitionUi[];
    typesStatus: RequestStatus;
    attributes: KnowledgeAttributeDefinitionUi[];
    attributesStatus: RequestStatus;
}

const initialState: KnowledgeDefinitionsState = {
    types: [],
    typesStatus: RequestStatus.NONE,
    attributes: [],
    attributesStatus: RequestStatus.NONE
};

const errorMessage = (error: any, fallback: string): string => error?.message || fallback;

export const fetchKnowledgeTypes = createAsyncThunk<KnowledgeTypeDefinitionUi[], undefined, { rejectValue: string }>(
    "knowledgeDefinitions/fetchTypes",
    async (_payload, {rejectWithValue}) => {
        try {
            return await fetchKnowledgeTypeDefinitions();
        } catch (error: any) {
            return rejectWithValue(errorMessage(error, "Failed to load knowledge types"));
        }
    }
);

export const fetchKnowledgeAttributes = createAsyncThunk<KnowledgeAttributeDefinitionUi[], undefined, { rejectValue: string }>(
    "knowledgeDefinitions/fetchAttributes",
    async (_payload, {rejectWithValue}) => {
        try {
            return await fetchKnowledgeAttributeDefinitions();
        } catch (error: any) {
            return rejectWithValue(errorMessage(error, "Failed to load attributes"));
        }
    }
);

const refetch = (kind: KnowledgeDefinitionKind, dispatch: (action: any) => unknown) => {
    if (kind === KnowledgeDefinitionKind.TYPE) {
        dispatch(fetchKnowledgeTypes());
    } else {
        dispatch(fetchKnowledgeAttributes());
    }
};

/** Creates (no publicId) or updates a definition, then reloads that list so it stays sorted. */
export const saveKnowledgeDefinition = createAsyncThunk<KnowledgeDefinitionUi, {
    kind: KnowledgeDefinitionKind;
    request: KnowledgeDefinitionRequest;
    publicId?: string;
}, { rejectValue: string }>(
    "knowledgeDefinitions/save",
    async ({kind, request, publicId}, {dispatch, rejectWithValue}) => {
        try {
            const saved = await saveKnowledgeDefinitionRequest(kind, request, publicId);
            refetch(kind, dispatch);
            return saved;
        } catch (error: any) {
            return rejectWithValue(errorMessage(error, `Failed to save the ${getKnowledgeDefinitionName(kind)}`));
        }
    }
);

export const deleteKnowledgeDefinition = createAsyncThunk<void, {
    kind: KnowledgeDefinitionKind;
    publicId: string;
}, { rejectValue: string }>(
    "knowledgeDefinitions/delete",
    async ({kind, publicId}, {dispatch, rejectWithValue}) => {
        try {
            await deleteKnowledgeDefinitionRequest(kind, publicId);
            refetch(kind, dispatch);
        } catch (error: any) {
            return rejectWithValue(errorMessage(error, `Failed to delete the ${getKnowledgeDefinitionName(kind)}. Please try again.`));
        }
    }
);

export const knowledgeDefinitionsSlice = createSlice({
    name: "knowledgeDefinitions",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchKnowledgeTypes.pending, (state) => {
                state.typesStatus = RequestStatus.PENDING;
            })
            .addCase(fetchKnowledgeTypes.fulfilled, (state, {payload}) => {
                state.types = payload;
                state.typesStatus = RequestStatus.RESOLVED;
            })
            .addCase(fetchKnowledgeTypes.rejected, (state) => {
                state.typesStatus = RequestStatus.REJECTED;
            })
            .addCase(fetchKnowledgeAttributes.pending, (state) => {
                state.attributesStatus = RequestStatus.PENDING;
            })
            .addCase(fetchKnowledgeAttributes.fulfilled, (state, {payload}) => {
                state.attributes = payload;
                state.attributesStatus = RequestStatus.RESOLVED;
            })
            .addCase(fetchKnowledgeAttributes.rejected, (state) => {
                state.attributesStatus = RequestStatus.REJECTED;
            });
    }
});

export const knowledgeTypesSelector = (state: RootState): KnowledgeTypeDefinitionUi[] => state.knowledgeDefinitions.types;
export const knowledgeTypesStatusSelector = (state: RootState): RequestStatus => state.knowledgeDefinitions.typesStatus;
export const knowledgeAttributesSelector = (state: RootState): KnowledgeAttributeDefinitionUi[] => state.knowledgeDefinitions.attributes;
export const knowledgeAttributesStatusSelector = (state: RootState): RequestStatus => state.knowledgeDefinitions.attributesStatus;
